import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import {
  EmailInputFormField,
  InputFormField,
  NativeSelectFormField,
  SwitchFormField,
} from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { useTranslation } from '@web/src/lib/intl/react'
import { availableThemes } from '@web/src/theme'
import { forwardRef, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { UpdateUserMutation, UpdateUserMutationVariables } from 'types/graphql'

interface UserProps {
  id: string
  email?: string
  roles?: string
  roleExpiresAt?: string
  emailVerified?: boolean
  name?: string | null
  theme?: string | null
  seasonalThemesEnabled?: boolean | null
  betaProgram?: boolean | null
  profile?: {
    deletedAt?: string
  }
}

const UPDATE_USER: TypedDocumentNode<UpdateUserMutation, UpdateUserMutationVariables> = gql`
  mutation UpdateUserMutation($id: String!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      email
      createdAt
      roles
      emailVerified
      roleExpiresAt
      bannedUntil
      theme
      seasonalThemesEnabled
      betaProgram
    }
  }
`

interface AccountEditProps {
  user: UserProps
  className?: string
  onAccountUpdated?: () => void | Promise<void>
}

const AccountEdit = forwardRef<HTMLDivElement, AccountEditProps>((props, forwardedRef) => {
  const { t } = useTranslation()
  const formRef = useRef<HTMLFormElement>(null)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [saveModal, setSaveModal] = useState(false)
  const { user, className, onAccountUpdated } = props

  const formMethods = useForm({
    defaultValues: {
      email: user?.email,
      roles: user?.roles,
      roleExpiresAt: user?.roleExpiresAt ? new Date(user?.roleExpiresAt).toISOString().substring(0, 10) : null,
      name: user?.name ?? '',
      theme: user?.theme ?? 'dark',
      seasonalThemesEnabled: user?.seasonalThemesEnabled ?? true,
      emailVerified: Boolean(user?.emailVerified),
      betaProgram: user?.betaProgram !== false,
    },
  })

  const { isDirty } = useFormState({ control: formMethods.control })

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: async () => {
      toast.success(t('User updated'))
      setShowSpinnerValue(false)
      await onAccountUpdated?.()
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const handleSubmit = (fieldData: {
    email: string
    roles: string
    roleExpiresAt: string | null
    name: string
    theme: string
    seasonalThemesEnabled: boolean
    emailVerified: boolean
    betaProgram: boolean
  }) => {
    setShowSpinnerValue(true)
    setSaveModal(false)

    const input: UpdateUserMutationVariables['input'] = {
      email: fieldData.email,
      roles: fieldData.roles,
      roleExpiresAt: fieldData.roleExpiresAt ? new Date(fieldData.roleExpiresAt).toISOString() : null,
      theme: fieldData.theme,
      seasonalThemesEnabled: fieldData.seasonalThemesEnabled,
      betaProgram: fieldData.betaProgram,
    }

    if (fieldData.email !== user?.email) {
      input.emailVerified = false
    } else if (fieldData.emailVerified !== Boolean(user?.emailVerified)) {
      input.emailVerified = fieldData.emailVerified
    }

    updateUser({
      variables: {
        id: user.id,
        input,
      },
    })
  }

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit} ref={formRef} style={{ width: '100%' }}>
      <div className={cn('flex w-full flex-col gap-4', className)}>
        <Card className="w-full" ref={forwardedRef}>
          <CardHeader>
            <CardTitle>{t('Account details')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <EmailInputFormField
              inputProps={{ autoComplete: 'off', disabled: Boolean(user?.profile?.deletedAt) }}
              label={t('Email')}
              name="email"
              required
            />

            <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
              <NativeSelectFormField disabled={Boolean(user?.profile?.deletedAt)} label={t('Membership')} name="roles">
                <option value="admin">{t('Admin')}</option>
                <option value="member">{t('Member')}</option>
                <option value="moderator">{t('Moderator')}</option>
                <option value="preview">{t('Preview')}</option>
                <option value="supporter">{t('Supporter')}</option>
                <option value="tradeshow">{t('Tradeshow')}</option>
              </NativeSelectFormField>

              <InputFormField
                inputProps={{ type: 'date', readOnly: Boolean(user?.profile?.deletedAt) }}
                label={t('Membership expiry')}
                name="roleExpiresAt"
              />
            </div>

            <NativeSelectFormField disabled={Boolean(user?.profile?.deletedAt)} label={t('Theme')} name="theme">
              {availableThemes(true, t).map((theme) => (
                <option key={theme.name} value={theme.name}>
                  {theme.label}
                </option>
              ))}
            </NativeSelectFormField>
            <SwitchFormField
              disabled={Boolean(user?.profile?.deletedAt)}
              label={t('Seasonal themes')}
              name="seasonalThemesEnabled"
            />

            <div className="flex flex-col gap-4 rounded-lg border border-border bg-muted/30 p-4">
              <SwitchFormField
                disabled={Boolean(user?.profile?.deletedAt)}
                label={t('Email Verified')}
                name="emailVerified"
              />
              <SwitchFormField
                disabled={Boolean(user?.profile?.deletedAt)}
                helperText={t('Controls product feedback and experimental entry points.')}
                label={t('Beta Program')}
                name="betaProgram"
              />
            </div>
          </CardContent>
        </Card>
        <div className="flex w-full justify-end">
          <Button disabled={!isDirty || showSpinner} onClick={() => setSaveModal(true)} type="button">
            {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
            {t('Save')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        confirmText={t('Save')}
        onConfirm={() => formRef.current?.requestSubmit()}
        onOpenChange={(e) => setSaveModal(e.open)}
        open={saveModal}
        text={t('Are you sure you want to save these account changes?')}
        title={t('Save account changes?')}
        variant="basic"
      />
    </Form>
  )
})

AccountEdit.displayName = 'AccountEdit'

export default AccountEdit
